import { useRouter } from 'next/router';
import { getUserHasCollateral, getUserHasDebt } from 'queries/decoratedSilo';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { BorrowPanelController } from '../borrow/BorrowPanelController';
import { DepositPanelController } from '../deposit/DepositPanelController';
import { RepayPanelController } from '../repay/RepayPanelController';
import { WithdrawPanelController } from '../withdraw/WithdrawPanelController';
import { Screen, validScreens } from './shared';
import { useThisDecoratedSilo } from './hooks';
import { updateURLSearchParams } from 'app/functions/url-search-params';
import { ChartsPanel } from '../charts/ChartsPanel';
import { isNull } from '@silo/utils';
import { ErrorBoundary } from 'react-error-boundary';
import { useIsChartsFeatureOn } from '../common/ActionForm/useIsChartsFeatureOn';
import { ChartIcon, Tab, Tabs } from '@silo/ui';

export const ActionTabs: FC = () => {
  const router = useRouter();
  const decoratedSilo = useThisDecoratedSilo();
  const userHasCollateral = getUserHasCollateral(decoratedSilo);
  const userHasDebt = getUserHasDebt(decoratedSilo);
  const isChartsFeatureOn = useIsChartsFeatureOn();

  const onTabsChange = useCallback(
    (value: Screen | null) => {
      const newPath = updateURLSearchParams((params) => {
        if (isNull(value)) {
          params.delete('screen');
        } else {
          params.set('screen', value);
        }

        return params;
      })(router.asPath);

      router.replace(newPath, undefined, { shallow: true });
    },
    [router]
  );

  /**
   * When any asset in the market has an unavailable oracle price (price 0), we
   * don't let users open new positions (deposit/borrow), since LTV/health-factor
   * math is meaningless without a price. Withdraw/repay (exit actions) stay
   * available so users can always access their funds.
   */
  const pricingUnavailable = decoratedSilo.assets.some((asset) => asset.priceInNative.isZero());

  const data = useMemo(() => {
    const _data: Tab<Screen>[] = pricingUnavailable
      ? []
      : [
          {
            value: 'deposit',
            label: 'Deposit',
            content: (
              <ErrorBoundary
                fallback={
                  <div className="p-4 rounded-b-lg bg-shale-800">
                    A system error has occured that prevents depositing. Please contact us for assistance if the error
                    persists.
                  </div>
                }
              >
                <DepositPanelController />
              </ErrorBoundary>
            ),
            'data-cy': 'deposit-chip',
          },
          {
            value: 'borrow',
            label: 'Borrow',
            content: (
              <ErrorBoundary
                fallback={
                  <div className="p-4 rounded-b-lg bg-shale-800">
                    A system error has occured that prevents borrowing. Please contact us for assistance if the error
                    persists.
                  </div>
                }
              >
                <BorrowPanelController />
              </ErrorBoundary>
            ),
            'data-cy': 'borrow-chip',
          },
        ];

    if (userHasCollateral) {
      _data.push({
        value: 'withdraw',
        label: 'Withdraw',
        content: (
          <ErrorBoundary
            fallback={
              <div className="p-4 rounded-b-lg bg-shale-800">
                A system error has occured that prevents withdrawing. Please contact us for assistance if the error
                persists.
              </div>
            }
          >
            <WithdrawPanelController />
          </ErrorBoundary>
        ),
        'data-cy': 'withdraw-chip',
      });
    }

    if (userHasDebt) {
      _data.push({
        value: 'repay',
        label: 'Repay',
        content: (
          <ErrorBoundary
            fallback={
              <div className="p-4 rounded-b-lg bg-shale-800">
                A system error has occured that prevents repaying. Please contact us for assistance if the error
                persists.
              </div>
            }
          >
            <RepayPanelController />
          </ErrorBoundary>
        ),
        'data-cy': 'repay-chip',
      });
    }

    if (isChartsFeatureOn) {
      _data.push({
        value: 'charts',
        label: (
          <span className="flex gap-1.5 items-center">
            <ChartIcon className="w-5 h-5 lg:w-6 lg:h-6" />
            <span>Charts</span>
          </span>
        ),
        content: (
          <ErrorBoundary
            fallback={
              <div className="p-4 rounded-b-lg bg-shale-800">
                A system error has occured. Please contact us for assistance if the error persists.
              </div>
            }
          >
            <ChartsPanel />
          </ErrorBoundary>
        ),
        'data-cy': 'charts-chip',
        'aria-label': 'Charts',
      });
    }

    return _data;
  }, [pricingUnavailable, userHasCollateral, userHasDebt, isChartsFeatureOn]);

  const activeTab = useMemo(() => {
    if (isScreen(data)(router.query.screen)) return router.query.screen;

    // Fall back to the first available tab. When pricing is unavailable, 'deposit'
    // isn't present, so withdraw/repay becomes the default instead of nothing.
    return data[0]?.value ?? 'deposit';
  }, [router.query, data]);

  useEffect(() => {
    if (router.query.screen && !isScreen(data)(router.query.screen)) {
      const newPath = updateURLSearchParams((params) => {
        params.delete('screen');
        return params;
      })(router.asPath);

      router.replace(newPath, undefined, { shallow: true });
    }
  }, [router, data]);

  return (
    <>
      {pricingUnavailable && (
        <div className="p-4 mb-2.5 rounded-lg bg-shale-800 text-shale-200">
          Pricing is temporarily unavailable for this market. You can still withdraw or repay your position.
        </div>
      )}

      <Tabs
        id="action-tabs"
        data={data}
        value={activeTab}
        onChange={onTabsChange}
        tabsContainerClassName="py-5 px-5 rounded-t-lg bg-shale-800"
      />
    </>
  );
};

const isScreen =
  (data: Tab<Screen>[]) =>
  (value: any): value is Screen =>
    Boolean(validScreens.includes(value) && data.find((datum) => datum.value === value));
